import React, { useState, useEffect, Suspense } from 'react';

// Toast Wrapper mit korrekten Defaults (NO monkey-patching)
import { NotificationToastContainer, toast } from './components/notifications';
import { NetworkStatus } from './components/ui/NetworkOptimization';

import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ConfirmProvider } from './components/ui/ConfirmProvider.jsx';
import { authorizedFetch } from './utils/api';
import { clearAuthStorage, decodeJwtPayload } from './utils/auth';
import { MainScreen } from './components/MainScreen';
import PushNotificationService from './services/PushNotificationService';
import { applyStatusBarTheme } from './utils/nativeStatusBar';
import { installKeyboardReveal } from './utils/keyboardReveal';
import { ensureNativeKeyboardResize } from './hooks/useChatKeyboard';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import AuthPage from './components/AuthPage';
import ProfileSetup from './components/ProfileSetup';
import LoadingSpinner from './components/ui/LoadingSpinner';
import RouteFallback from './components/ui/RouteFallback';
import LogoSplash from './components/ui/LogoSplash';
import LandingPage from './components/landingpage/LandingPage';
// HINWEIS: Demo-Seiten (SkillSelectorDemo, LearningJournalDemo, ProfileCardV2Demo)
//          wurden entfernt (bis Aug 2026 unter src/_archive/landingpage/, jetzt nur
//          noch in der Git-Historie) – Routen entfernt.
import RoleRoute from './components/RoleRoute';
import DeepLinkHandler from './components/DeepLinkHandler';
import ErrorBoundary from './components/ui/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
// LTI-Integration
import LtiSession from './pages/LtiSession';
// Schulregistrierung
import SchoolConfirmationPage from './pages/SchoolConfirmationPage';
// Demo Hub
// FIX (19. Okt 2025): DashboardLayout NICHT lazy, da es lazy Children hat
// Verhindert React Error #426 (Suspense boundary issues)
import DashboardLayout from './components/dashboards/DashboardLayout';
// Constants imports
import './App.css';

// Stabile Referenzen für MainScreen-Props.
// Inline-Arrays ([] in JSX) wären bei jedem Render eine neue Referenz und würden React.memo brechen.
const EMPTY_PROFILES = [];

// Nur in der Entwicklung loggen (Muster wie in MainScreen.js): Das Profil-Setup
// gibt Name, Alter, Klasse, Bio und E-Mail aus. Im Produktions-Build standen diese
// Daten sonst in der Konsole und gingen als Sentry-Breadcrumb mit dem nächsten Fehler raus.
const debugLog = process.env.NODE_ENV === 'development' ? console.log.bind(console) : () => { };

// Lazy-Import mit Selbstheilung: Nach einem Deployment existieren alte
// Chunk-Dateien nicht mehr (rsync) - offene Tabs crashen dann bei jeder
// Lazy-Navigation mit ChunkLoadError ("unerwarteter Fehler"). Ein einmaliges
// Neuladen holt das aktuelle Bundle (sessionStorage-Guard gegen Reload-Loops).
const lazyWithRetry = (importFn) => React.lazy(() =>
  importFn().catch((error) => {
    try {
      const alreadyReloaded = sessionStorage.getItem('lazy-chunk-reloaded');
      if (!alreadyReloaded) {
        sessionStorage.setItem('lazy-chunk-reloaded', String(Date.now()));
        window.location.reload();
        // Pending Promise: Seite laedt gleich neu, nichts mehr rendern
        return new Promise(() => {});
      }
    } catch (storageError) {
      // sessionStorage nicht verfuegbar - Fehler normal durchreichen
    }
    throw error;
  })
);

// Lazy Loading für bessere Performance
const TeacherDashboard = lazyWithRetry(() => import('./components/dashboards/TeacherDashboard'));
const SchoolAdminDashboard = lazyWithRetry(() => import('./components/dashboards/SchoolAdminDashboard'));
// Einrichtungs-Assistent nur für das Admin-Onboarding-Gate (Okt 2026): Statisch
// importiert zog er rund 120 KB Admin-Code samt papaparse in den Haupt-Chunk
// jeder Schülerin. SchoolAdminDashboard lädt ihn ohnehin aus seinem Chunk.
const SchoolSetupPanel = lazyWithRetry(() => import('./components/dashboards/SchoolSetupPanel'));
const SuperAdminDashboard = lazyWithRetry(() => import('./components/dashboards/SuperAdminDashboard'));
const WelcomeTour = lazyWithRetry(() => import('./components/welcome/WelcomeTour'));
const ResearchConsentGate = lazyWithRetry(() => import('./components/legal/ResearchConsentGate'));
const DesignSystemPage = lazyWithRetry(() => import('./pages/DesignSystemPage'));
// SessionsPage und das Logbuch (V2) werden hier nicht mehr lazy geladen: Sie sind
// Ansichten von MainScreen, das sie statisch importiert. Beides gleichzeitig hiess
// zwei Ladewege fuer dasselbe Modul.
// V1 des Logbuchs (pages/LearningJournalPage.js) bleibt als Sicherheitsnetz im
// Repo — Rollback = Import in MainScreen.js zurücktauschen.
const ImpressumPage = lazyWithRetry(() => import('./pages/ImpressumPage'));
const DatenschutzPage = lazyWithRetry(() => import('./pages/DatenschutzPage'));
// Konto löschen: öffentliche Löschanleitung (Pflicht für Google Play, Okt 2026)
const KontoLoeschenPage = lazyWithRetry(() => import('./pages/KontoLoeschenPage'));
// Android-Beta: Anleitung für den geschlossenen Play-Test über die Google Group (Okt 2026)
const AndroidBetaPage = lazyWithRetry(() => import('./pages/AndroidBetaPage'));
const UnsubscribePage = lazyWithRetry(() => import('./pages/UnsubscribePage'));
const AGBPage = lazyWithRetry(() => import('./pages/AGBPage'));
const AVVPage = lazyWithRetry(() => import('./pages/AVVPage'));
// Neue Seiten für Landingpage-Reorganisation
const PedagogyPage = lazyWithRetry(() => import('./pages/PedagogyPage'));
const AdministrationPage = lazyWithRetry(() => import('./pages/AdministrationPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
// Geteiltes Lernlogbuch (oeffentlich, schreibgeschuetzt; der Token in der URL ist der Schluessel)
const SharedJournalPage = lazyWithRetry(() => import('./pages/SharedJournalPage'));
const FaqPage = lazyWithRetry(() => import('./pages/FaqPage'));
// Werkstatt: Archiv der Werkstattbriefe + Newsletter-Anmeldung (Double-Opt-in, Sep 2026)
const WerkstattPage = lazyWithRetry(() => import('./pages/WerkstattPage'));
const WerkstattIssuePage = lazyWithRetry(() => import('./pages/WerkstattIssuePage'));
const NewsletterConfirmPage = lazyWithRetry(() => import('./pages/NewsletterConfirmPage'));
const NewsletterUnsubscribePage = lazyWithRetry(() => import('./pages/NewsletterUnsubscribePage'));
// Sentry Test Page (Development/Testing)
const SentryTestPage = lazyWithRetry(() => import('./pages/SentryTestPage'));
// DSGVO Settings (User Privacy & Data Management)
const DsgvoSettings = lazyWithRetry(() => import('./components/settings/DsgvoSettings'));
// 🆕 Mobile-First Pages (Modal → Page Migration, 24. Okt 2025)
const CreateSessionPage = lazyWithRetry(() => import('./pages/CreateSessionPage'));
const BarcampPlannerPage = lazyWithRetry(() => import('./pages/BarcampPlannerPage'));
const JoinSessionPage = lazyWithRetry(() => import('./pages/JoinSessionPage'));
const SessionDetailPage = lazyWithRetry(() => import('./pages/SessionDetailPage')); // 🆕 Session Detail Page (31. Okt 2025)
const SessionFeedbackPage = lazyWithRetry(() => import('./pages/SessionFeedbackPage')); // 🆕 Lernlogbuch 2.0: nachträgliches Feedback (Jun 2026)
const EditSessionPage = lazyWithRetry(() => import('./pages/EditSessionPage'));
const FocusSessionPage = lazyWithRetry(() => import('./pages/FocusSessionPage')); // 🆕 Focus Session Page (5. Nov 2025)
const ChatPageRoute = lazyWithRetry(() => import('./pages/ChatPageRoute'));
const ReportMessagePage = lazyWithRetry(() => import('./pages/ReportMessagePage'));
const SkillRequestPage = lazyWithRetry(() => import('./pages/SkillRequestPage'));
const AccountSettingsPage = lazyWithRetry(() => import('./pages/AccountSettingsPage'));
const ChangePasswordPage = lazyWithRetry(() => import('./pages/ChangePasswordPage'));
const AppearanceSettingsPage = lazyWithRetry(() => import('./pages/AppearanceSettingsPage'));
const ApiTokensPage = lazyWithRetry(() => import('./pages/ApiTokensPage'));
const ConnectAssistantPage = lazyWithRetry(() => import('./pages/ConnectAssistantPage'));
const ConnectedAssistantsPage = lazyWithRetry(() => import('./pages/ConnectedAssistantsPage'));
const BlockedUsersPage = lazyWithRetry(() => import('./pages/BlockedUsersPage'));
const FeedbackPage = lazyWithRetry(() => import('./pages/FeedbackPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const ProfileEditPage = lazyWithRetry(() => import('./pages/ProfileEditPage'));
const ConnectPage = lazyWithRetry(() => import('./pages/ConnectPage'));
const AuraStatsPage = lazyWithRetry(() => import('./pages/AuraStatsPage'));
const OtterWardrobePage = lazyWithRetry(() => import('./pages/OtterWardrobePage.jsx'));
const QRScannerPage = lazyWithRetry(() => import('./pages/QRScannerPage'));
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'));
const NotificationSettingsPage = lazyWithRetry(() => import('./pages/NotificationSettingsPage'));

// Komponente für das globale Fehlerhandling
const AuthErrorHandler = () => {
  const { token, user, loading } = useAuth();
  const { resolvedTheme } = useTheme();

  // StatusBar (iOS + Android) folgt dem aktiven Theme, siehe utils/nativeStatusBar.js
  useEffect(() => {
    const isNativeApp = Capacitor.isNativePlatform();
    if (isNativeApp) {
      document.documentElement.classList.add('native-app');
      document.body.classList.add('native-app');
    }

    // Beim ersten Aufruf kann die Theme-Klasse noch fehlen (ThemeProvider setzt
    // sie in einem eigenen Effect) — deshalb der zweite Versuch nach 800 ms.
    // Nach dem Ausblenden des Splash-Screens und nach jeder Rueckkehr aus dem
    // Hintergrund faellt Android auf das Fenster-Theme zurueck; der
    // appStateChange-Listener stellt das Design danach wieder her.
    const setStatusBarStyle = () => applyStatusBarTheme(resolvedTheme);
    let appStateListener = null;
    let disposed = false;
    if (isNativeApp) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) setStatusBarStyle();
      }).then((listener) => {
        if (disposed) listener.remove();
        else appStateListener = listener;
      }).catch(() => {});
    }
    setStatusBarStyle();
    const retryTimer = setTimeout(setStatusBarStyle, 800);
    window.addEventListener('focus', setStatusBarStyle);
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      appStateListener?.remove();
      window.removeEventListener('focus', setStatusBarStyle);
      if (isNativeApp) {
        document.documentElement.classList.remove('native-app');
        document.body.classList.remove('native-app');
      }
    };
  }, [resolvedTheme]);

  // Tastatur: body.keyboard-open setzen (blendet die Bottom-Navigation aus) und
  // das angetippte Feld sichtbar halten — auf iOS UND Android. Bis Oktober 2026
  // lief das Scrollen nur auf Android ("iOS scrollt selbst"); das Tastatur-Plugin
  // haelt WebKit aber davon ab. Hintergrund in utils/keyboardReveal.js.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    // Chat schaltet auf iOS resize 'none' (hooks/useChatKeyboard). Nach einem
    // harten Neuladen aus dem Chat heraus stuende das noch — hier zuruecksetzen.
    ensureNativeKeyboardResize({ platform: Capacitor.getPlatform(), keyboard: Keyboard });
    return installKeyboardReveal({ Keyboard, platform: Capacitor.getPlatform() });
  }, []);

  // SplashScreen erst hiden, wenn Auth-Loading durch ist
  // (verhindert leeres Flackern auf iOS, weil der native Splash sonst hart nach 3s ausgeblendet wird)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (loading) return;
    const id = requestAnimationFrame(() => {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    });
    return () => cancelAnimationFrame(id);
  }, [loading]);

  useEffect(() => {
    const handleAuthError = (event) => {
      const { message } = event.detail || {};

      // Egal, wer das Event ausgelöst hat (api.js, ApiService, SessionManager): alle
      // Auth-Daten und Push-Listener räumen, damit auf einem geteilten Gerät nach dem
      // Reload nicht das alte Profil aus localStorage hydriert wird.
      clearAuthStorage();
      PushNotificationService.cleanup().catch(() => {});

      toast.error(message || 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.', {
        position: "top-right",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
        pauseOnFocusLoss: false
      });

      // Nach 3 Sekunden zur Login-Seite weiterleiten
      setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
    };

    // Event-Listener für Auth-Fehler hinzufügen
    window.addEventListener('auth:error', handleAuthError);

    // FIX (16. Okt 2025): Prüfe nur bei TOKEN-ÄNDERUNGEN, nicht bei jedem Mount
    // Sonst wird Landingpage immer zu /login weitergeleitet, bevor React Router läuft
    // Nur reagieren wenn:
    // 1. Token wurde ENTFERNT (user war eingeloggt, jetzt nicht mehr)
    // 2. UND wir sind auf einer geschützten Route

    // '/profile-setup' und '/welcome' gehoeren hier zwingend rein: Das Onboarding laeuft
    // teils bevor der Token-State propagiert ist. Ohne sie wurde der User mitten im
    // Profil-Setup per window.location.href auf /login geworfen.
    const publicRoutes = ['/login', '/register', '/scan-invitation', '/', '/demo', '/impressum', '/datenschutz', '/konto-loeschen', '/android', '/agb', '/avv', '/ueber-mich', '/faq', '/confirm-school', '/start', '/paedagogik', '/administration', '/sentry-test', '/forgot-password', '/reset-password', '/profile-setup', '/welcome', '/shared'];
    // Nur exakt: /connect (KI-Assistent verbinden) ist öffentlich, /connect/:id nicht
    const exactPublicRoutes = ['/connect'];
    const isPublicRoute = exactPublicRoutes.includes(window.location.pathname) || publicRoutes.some(route =>
      window.location.pathname === route ||
      window.location.pathname.startsWith(route + '/') ||
      window.location.pathname.startsWith('/public')
    );

    // Nur weiterleiten wenn Token verloren ging UND User war vorher eingeloggt.
    // Der React-State `token` kann dem localStorage hinterherhinken (z.B. direkt nach
    // Login/Registrierung). Deshalb beide Quellen pruefen - sonst reisst ein reiner
    // Zustands-Verzug den User aus der App.
    const hasToken = token || localStorage.getItem('token');

    if (!hasToken && user && !isPublicRoute) {
      console.warn('Token verloren auf geschützter Route, leite zur Login-Seite weiter');

      // Kein Toast mehr: Der harte Reload auf /login kam nach 500 ms und schnitt
      // ihn ohnehin ab - die Login-Seite selbst ist die Information.
      setTimeout(() => {
        window.location.href = '/login';
      }, 500);
    }

    return () => {
      window.removeEventListener('auth:error', handleAuthError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Nur token als Dependency, um Endlos-Loop zu vermeiden

  return null; // Diese Komponente rendert nichts
};

// Komponente für globale Session-Event-Behandlung
const SessionEventHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Lazy-load SessionManager nur wenn benötigt
    let sessionManager = null;

    const loadSessionManager = async () => {
      try {
        const module = await import('./services/SessionManager');
        sessionManager = module.default;

        // Event-Listener für Session-Events registrieren
        const removeListener = sessionManager.addEventListener((eventType, data) => {
          if (eventType === 'sessionCancelled') {
            console.log('Global Session Cancelled Event empfangen:', data);

            // Zeige Toast-Benachrichtigung
            toast.info(data.message || 'Die Session wurde abgebrochen.', {
              position: "top-right",
              autoClose: 5000
            });

            // Navigiere zur Sessions-Seite
            // Kurze Verzögerung damit Toast sichtbar bleibt
            setTimeout(() => {
              navigate('/sessions');
            }, 1500);
          }
        });

        return removeListener;
      } catch (error) {
        console.error('Fehler beim Laden des SessionManagers:', error);
        return null;
      }
    };

    let removeListener = null;
    loadSessionManager().then(cleanup => {
      removeListener = cleanup;
    });

    // Cleanup
    return () => {
      if (removeListener) {
        removeListener();
      }
    };
  }, [navigate]);

  return null; // Diese Komponente rendert nichts
};

// Einzige Render-Stelle fuer das Onboarding (Route /profile-setup).
// Frueher gab es ZWEI Stellen: das Gate in AppContent (ausserhalb von <Routes>) und diese
// Route. Kippte showProfileSetup waehrend des Setups auf false, wechselte React von der einen
// auf die andere Stelle -> Remount -> Schritt 0 mit leeren Feldern. Jetzt rendert nur diese
// Route; das Gate leitet lediglich per <Navigate> hierher.
// Die Zutritts-Entscheidung faellt EINMAL beim Mounten: Wer mit fertigem Profil hier landet
// (Reload/Tab-Restore auf /profile-setup), geht direkt in die App. Waehrend des laufenden
// Setups darf ein spaeterer Flag-Wechsel den Schritt-Zustand dagegen nicht mehr zerstoeren.
// Geschützte Route ohne Anmeldung: auf /login und das Ziel als state.from mitgeben.
// AuthPage springt nach dem Login nur zu Pfaden zurück, die utils/returnPath.js
// erlaubt (Okt 2026: Links aus dem Chat mit einem KI-Assistenten, /connect).
const LoginRedirect = () => {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
};

const ProfileSetupRoute = ({ user, showProfileSetup, onComplete, onFinish }) => {
  const [decision] = useState(() => {
    if (!user) {
      return localStorage.getItem('token') ? 'setup' : 'login';
    }
    // Admins haben hier nichts verloren: Ihr Onboarding ist der Einrichtungsassistent.
    // Ein leerer Skill-Katalog machte diesen Schritt fuer sie sonst zur Sackgasse
    // (Weiter verlangt Skills, Skills legt nur das Dashboard an).
    if (['ADMIN', 'SUPERADMIN'].includes(user.role)) return 'dashboard';
    if (showProfileSetup || user.requiresProfileSetup !== false) return 'setup';
    return 'app';
  });

  if (decision === 'login') return <Navigate to="/login" replace />;
  if (decision === 'app') return <Navigate to="/app" replace />;
  if (decision === 'dashboard') {
    return <Navigate to={user.role === 'SUPERADMIN' ? '/dashboard/super-admin' : '/dashboard/admin'} replace />;
  }
  return <ProfileSetup onComplete={onComplete} onFinish={onFinish} />;
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    token,
    getUser,
    loading,
    showProfileSetup,
    updateUserProfile,
    setShowProfileSetup,
    getUserRole, // Neue Rolle-bezogene Funktion
    hasMinRole   // Neue Rolle-bezogene Funktion
  } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);

  // handleSessionJoin ist hier entfallen: Er versorgte ausschliesslich die
  // /sessions-Route, die jetzt auf die Hauptansicht zeigt. Den Join-Flow liefert
  // dort MainScreen (handleJoinSession) — dieselbe Funktion aus
  // services/sessionJoinActions, nur an einer Stelle statt an zweien.

  // Push-Notification Tap → Navigation
  useEffect(() => {
    const handleNotificationTap = (event) => {
      const { actionUrl, type, chatId, sessionId } = event.detail || {};

      if (actionUrl) {
        navigate(actionUrl);
        return;
      }

      switch (type) {
        case 'CHAT_MESSAGE':
          if (chatId) navigate(`/app?view=chat&chatId=${chatId}`);
          break;
        case 'CHAT_REQUEST':
          navigate('/app?view=chat-overview');
          break;
        case 'CONNECTION_ACCEPTED':
          if (chatId) navigate(`/app?view=chat&chatId=${chatId}`);
          break;
        case 'SESSION_INVITED':
        case 'SESSION_START':
        case 'SESSION_COMPLETED':
        case 'SESSION_REMINDER':
          if (sessionId) navigate(`/session/${sessionId}`);
          else navigate('/sessions');
          break;
        // Abgesagte Session: nicht in die tote Session, sondern zur Liste
        case 'SESSION_CANCELLED':
          navigate('/sessions');
          break;
        default:
          navigate('/notifications');
      }
    };

    window.addEventListener('notification-tap', handleNotificationTap);
    return () => window.removeEventListener('notification-tap', handleNotificationTap);
  }, [navigate]);

  // Überprüfe Auth-Status beim App-Start
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          // Prüfe, ob das Token gültig ist
          try {
            // Dekodiere den Token-Payload (base64URL-fest; atob wirft sonst bei
            // '-'/'_' im Payload -> eingeloggter User wurde faelschlich ausgeloggt).
            const tokenPayload = decodeJwtPayload(storedToken);
            if (!tokenPayload) {
              throw new Error('Ungültiges Token-Format');
            }

            // Prüfe, ob das Token abgelaufen ist
            const currentTime = Math.floor(Date.now() / 1000);
            if (tokenPayload.exp && tokenPayload.exp < currentTime) {
              console.warn('Token ist abgelaufen, leite zur Anmeldeseite weiter');
              clearAuthStorage();
              setIsAuthenticated(false);
              navigate('/login');
              return;
            }

            // Optimierung: Prüfe, ob wir die getUser-Funktion aufrufen müssen
            // Wenn wir bereits gültige Benutzerdaten haben, verwenden wir diese
            const parsedUser = JSON.parse(storedUser);
            const isUserDataValid = parsedUser && parsedUser.id && (parsedUser.name || (parsedUser.profile && parsedUser.profile.name));

            if (isUserDataValid) {
              console.log('Gültige Benutzerdaten aus localStorage gefunden, überspringe API-Aufruf');
              setIsAuthenticated(true);
              setCurrentUser(parsedUser);

              // 🆕 Initialize native push notifications
              PushNotificationService.initialize().catch(err => {
                console.warn('Push notification initialization failed:', err);
              });

              // Nur zu /app navigieren, wenn User auf Landing/Login/Register ist
              const currentPath = window.location.pathname;
              if (currentPath === '/' || currentPath === '/login' || currentPath === '/register') {
                navigate('/app');
              }

              // Prüfe, wann der letzte API-Aufruf war
              const lastUserFetch = localStorage.getItem('lastUserFetch');
              const now = Date.now();
              // Aktualisiere nur, wenn der letzte Aufruf älter als 10 Minuten ist oder nicht existiert
              if (!lastUserFetch || (now - parseInt(lastUserFetch, 10) > 10 * 60 * 1000)) {
                // Nach dem ersten Render können wir die Benutzerdaten aktualisieren
                // ohne die Benutzeroberfläche zu blockieren
                setTimeout(() => {
                  // silent=true: Hintergrund-Refresh darf NICHT den globalen loading-State
                  // setzen, sonst zeigt App.js den Vollbild-Spinner und der gesamte
                  // MainScreen unmountet/remountet (=kompletter Reload ~5s nach Laden).
                  getUser(true).then(updatedUser => {
                    if (updatedUser) {
                      setCurrentUser(updatedUser);
                      // Speichere den Zeitpunkt des letzten Aufrufs
                      localStorage.setItem('lastUserFetch', now.toString());
                    } else {
                      // getUser() liefert nur bei 401 oder fehlendem Token null.
                      // Nur dann ist Abmelden richtig.
                      clearAuthStorage();
                      setIsAuthenticated(false);
                      navigate('/login');
                    }
                  }).catch(error => {
                    // Hier landen Netzwerk-/Server-Fehler (getUser wirft dann).
                    // Die auf dem Bildschirm sichtbaren Daten stammen aus dem localStorage
                    // und sind weiterhin gueltig - ein Hintergrund-Refresh, der einmal
                    // scheitert, darf niemanden aus der App werfen.
                    console.warn('Hintergrund-Aktualisierung fehlgeschlagen, bleibe angemeldet:', error?.message || error);
                  });
                }, 5000); // Längerer Timeout, um genügend Zeit zwischen Aufrufen zu lassen
              } else {
                console.log('Überspringe Hintergrund-Update - letzter Aufruf war innerhalb der letzten 10 Minuten');
              }
            } else {
              // Nur wenn keine gültigen Daten vorhanden sind, rufen wir getUser direkt auf.
              // silent=true: Der AuthProvider rendert seine Children nur bei loading===false
              // (AuthContext.js). Ein nicht-stiller Aufruf wuerde hier den kompletten
              // App-Baum unmounten; der Initial-Spinner kommt ohnehin vom AuthProvider.
              const userData = await getUser(true);
              if (userData) {
                setIsAuthenticated(true);
                setCurrentUser(userData);

                // Initialize native push notifications
                PushNotificationService.initialize().catch(err => {
                  console.warn('Push notification initialization failed:', err);
                });

                // Nur zu /app navigieren, wenn User auf Landing/Login/Register ist
                const currentPath = window.location.pathname;
                if (currentPath === '/' || currentPath === '/login' || currentPath === '/register') {
                  navigate('/app');
                }
              } else {
                setIsAuthenticated(false);
                clearAuthStorage();
                navigate('/login');
              }
            }
          } catch (error) {
            console.error('Fehler bei der Token-Validierung:', error);
            clearAuthStorage();
            setIsAuthenticated(false);
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Fehler beim Überprüfen des Auth-Status:', error);
        setIsAuthenticated(false);
        clearAuthStorage();
        navigate('/login');
      }
    };

    checkAuthStatus();
  }, []);

  // Setze isAuthenticated und currentUser basierend auf dem User-Status
  useEffect(() => {
    if (user) {
      // Stelle sicher, dass der Benutzer alle erforderlichen Felder hat
      const completeUser = {
        ...user,
        id: user.id || '',
        auraLevel: user.auraLevel || 1,
        auraPoints: user.auraPoints || 0,
        auraStats: {
          dailyMinutes: 0,
          currentStreak: 0,
          sessionsCompleted: 0,
          loginStreak: 0,
          ...user.auraStats
        },
        verifiedSkills: user.verifiedSkills || [],
        schoolName: user.schoolName || 'EachOtter Demo Schule'
      };

      // Verhindere Render-Loop: Nur setzen wenn sich etwas geändert hat
      setCurrentUser(prevUser => {
        if (JSON.stringify(prevUser) === JSON.stringify(completeUser)) {
          return prevUser; // Kein Update, verhindert Re-Render
        }
        return completeUser;
      });

      if (!isAuthenticated) {
        setIsAuthenticated(true);

        // Initialize native push notifications
        PushNotificationService.initialize().catch(err => {
          console.warn('Push notification initialization failed:', err);
        });
      }
    } else if (isAuthenticated || currentUser) {
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  }, [user, isAuthenticated]); // FIX: currentUser entfernt um Endlos-Loop zu vermeiden

  const handleProfileSetupComplete = async (setupData) => {
    try {
      // Debug-Log für Setup-Daten
      debugLog('Setup-Daten erhalten:', setupData);

      // Hole den aktiven Token
      const activeToken = token || localStorage.getItem('token');
      debugLog('Aktiver Token:', activeToken ? activeToken.substring(0, 20) + '...' : 'Kein Token');

      if (!activeToken) {
        throw new Error('Kein Authentifizierungs-Token gefunden');
      }

      // Extrahiere die User-ID aus dem Token (base64URL-fest)
      const tokenPayload = decodeJwtPayload(activeToken);
      if (!tokenPayload) {
        throw new Error('Ungültiges Token-Format');
      }

      const userId = tokenPayload.sub;
      debugLog('Extrahierte UserId:', userId);

      // Hole den aktiven Benutzer.
      // WICHTIG silent=true: Ein nicht-stiller getUser() setzt den globalen loading-State.
      // Der AuthProvider rendert seine Children nur bei loading===false -> der komplette
      // App-Baum inkl. ProfileSetup wuerde mitten im Speichern unmounten. Genau daran ist
      // der Onboarding-Abschluss bisher gescheitert (Endlosschleife zurueck zu Schritt 0).
      // keepProfileSetup: Das Onboarding-Flag darf hier nicht kippen (siehe getUser).
      const updatedUser = await getUser(true, { keepProfileSetup: true });
      debugLog('Aktiver Benutzer:', updatedUser);

      if (!updatedUser) {
        throw new Error('Benutzer nicht gefunden');
      }

      // Verwende die SchoolId aus der Datenbank statt aus dem Token
      const schoolIdFromUser = updatedUser.schoolId || '4350eb19-7927-476b-9e81-62b511aabdbb';  // Aktualisierte Demo-School-ID als Fallback
      debugLog('Verwende SchoolId:', schoolIdFromUser);

      // Bereite die Profildaten vor
      const profileData = {
        userId: userId,
        displayName: setupData.name || updatedUser.name,
        schoolId: schoolIdFromUser,
        offers: setupData.offers || [],
        seeks: setupData.seeks || [],
        grade: setupData.grade || '',
        age: setupData.age || '',
        aboutMe: setupData.aboutMe || '',
        avatar: setupData.image,
        availability: setupData.availability || {}
      };

      debugLog('Sende Profildaten:', profileData);

      // Prüfe ob bereits ein Profil existiert
      const existingProfileResponse = await authorizedFetch(`/api/v1/profile/user/${userId}`, {
        headers: {
          'X-School-ID': schoolIdFromUser,
          'X-User-ID': userId
        }
      });

      let response;
      let method;
      let endpoint;

      if (existingProfileResponse.ok) {
        const existingProfile = await existingProfileResponse.json();
        debugLog('Existierendes Profil gefunden:', existingProfile);
        method = 'PUT';
        endpoint = `/api/v1/profile/${existingProfile.id}`;
      } else {
        method = 'POST';
        endpoint = `/api/v1/profile`;
      }

      debugLog(`Verwende ${method} Anfrage an ${endpoint}`);

      response = await authorizedFetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-School-ID': schoolIdFromUser,
          'X-User-ID': userId
        },
        body: JSON.stringify(profileData)
      });

      // Detaillierte Fehlerbehandlung
      if (!response.ok) {
        const errorData = await response.json();
        console.error(`Fehler beim ${method === 'PUT' ? 'Aktualisieren' : 'Erstellen'} des Profils:`, {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          error: errorData
        });
        throw new Error(errorData.error || `Fehler ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      debugLog('Profil erfolgreich verarbeitet:', result);

      // FIX: Hole User-Daten frisch vom Server statt updateUserProfile aufzurufen
      // updateUserProfile sendete einen zweiten PUT Request der die Skills überschrieb!
      // silent=true aus demselben Grund wie oben: kein Unmount des laufenden ProfileSetup.
      // keepProfileSetup=true: Nach dem Speichern meldet /me "Profil vollstaendig". Ohne
      // diese Option setzte getUser showProfileSetup=false -> AppContent wechselte von der
      // Setup-Ansicht auf die Routen -> ProfileSetup wurde neu gemountet -> Schritt 0 mit
      // leeren Feldern ("man muss wieder von vorne anfangen"). Das Flag wird erst in
      // handleProfileSetupFinish zurueckgesetzt, wenn die Erfolgsseite bestaetigt wurde.
      await getUser(true, { keepProfileSetup: true });

      // WICHTIG: Hier wird NICHT mehr navigiert. Das Speichern passiert jetzt schon beim
      // Klick auf "Profil erstellen" (vor der Erfolgsseite). So ist das Profil sicher in der
      // DB, bevor der App-Download-Hinweis erscheint – kein Datenverlust mehr, wenn der User
      // dort aussteigt. Die Navigation übernimmt handleProfileSetupFinish nach "Los geht's!".
    } catch (error) {
      console.error('Fehler im Profil-Setup:', error);
      setError(error.message || 'Ein unerwarteter Fehler ist aufgetreten');
      // Fehler weiterreichen, damit ProfileSetup auf dem Eingabe-Schritt bleibt
      // und der Entwurf erhalten bleibt.
      toast.error(error.message || 'Fehler beim Verarbeiten des Profils');
      throw error;
    }
  };

  // Abschluss nach der Erfolgsseite: Onboarding ausblenden + Willkommens-Tour starten.
  // Das eigentliche Speichern ist zu diesem Zeitpunkt bereits erfolgt.
  const handleProfileSetupFinish = () => {
    setShowProfileSetup(false);
    navigate('/welcome');
  };

  // Render nur MainScreen, wenn wir einen vollständig initialisierten Benutzer haben
  const renderMainScreen = () => {
    const hasProfileData = currentUser && currentUser.profile && (
      currentUser.profile.offers || currentUser.profile.seeks ||
      currentUser.profile.offeredSkills || currentUser.profile.soughtSkills
    );

    return (
      <MainScreen initialProfiles={EMPTY_PROFILES} />
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Admin-Onboarding-Gate: Schul-Admin wird beim Erst-Login in den Einrichtungs-Assistenten
  // geführt (schönes Onboarding "Willkommen bei EachOtter 🦦" mit KI-Skill-Katalog),
  // NICHT in den Schüler-Profil-Setup. Überspringbar via "Später" (sessionStorage-Flag).
  if (
    user
    && getUserRole() === 'ADMIN'
    && user.requiresSchoolSetup
    && !sessionStorage.getItem('schoolSetupDismissed')
  ) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <SchoolSetupPanel
          isOnboarding
          onFinish={() => { window.location.href = '/dashboard/admin'; }}
        />
      </Suspense>
    );
  }

  // Profil-Setup noetig (Schüler/Lehrer): auf die Onboarding-Route schicken.
  // Bewusst KEIN direktes <ProfileSetup/> hier - das Setup wird ausschliesslich von der
  // Route /profile-setup gerendert (siehe ProfileSetupRoute), damit ein Flag-Wechsel
  // waehrend des Setups keinen Remount ausloest.
  const isAdminRole = ['ADMIN', 'SUPERADMIN'].includes(getUserRole());
  // /connect (Zustimmung für einen KI-Assistenten) braucht kein fertiges Profil; die
  // Anfrage verfiele sonst während des Setups (rid gilt 10 Minuten).
  if (user && showProfileSetup && !isAdminRole && location.pathname !== '/profile-setup'
    && location.pathname !== '/connect') {
    return <Navigate to="/profile-setup" replace />;
  }

  // Funktion zur Bestimmung des richtigen Dashboard-Links basierend auf Benutzerrolle
  const getDashboardPath = () => {
    if (!user) return '/app';

    const role = getUserRole();

    switch (role) {
      case 'TEACHER':
        return '/dashboard/teacher';
      case 'ADMIN':
        return '/dashboard/admin';
      case 'SUPERADMIN':
        return '/dashboard/super-admin';
      default:
        return '/app'; // STUDENT und andere Rollen -> normale App
    }
  };

  // Dashboard-Weiterleitungsfunktion für Umleitung zur passenden Dashboard-Seite
  const redirectToDashboard = () => {
    const dashboardPath = getDashboardPath();
    return <Navigate to={dashboardPath} replace={true} />;
  };

  return (
    <>
      <AuthErrorHandler />
      <SessionEventHandler />
      <Routes>
        {/* Öffentliche Routen */}
        {/* In der nativen Capacitor App direkt Login anzeigen, im Browser die Landing Page */}
        <Route path='/' element={
          Capacitor.isNativePlatform()
            ? <AuthPage mode="login" />
            : <LandingPage />
        } />
        {/* Demo-Seiten entfernt (nur noch in der Git-Historie) – Routen entfernt */}

        {/* Neue Landingpage-Unterseiten */}
        <Route path='/start' element={
          <Suspense fallback={<RouteFallback />}>
            <PedagogyPage />
          </Suspense>
        } />
        {/* Redirect von altem Pfad */}
        <Route path='/paedagogik' element={<Navigate to="/start" replace />} />
        <Route path='/administration' element={
          <Suspense fallback={<RouteFallback />}>
            <AdministrationPage />
          </Suspense>
        } />
        <Route path='/ueber-mich' element={
          <Suspense fallback={<RouteFallback />}>
            <AboutPage />
          </Suspense>
        } />
        <Route path='/faq' element={
          <Suspense fallback={<RouteFallback />}>
            <FaqPage />
          </Suspense>
        } />
        {/* Werkstatt: Archiv + Newsletter (Bestaetigen/Abmelden kommen aus den Mail-Links, ohne Login) */}
        <Route path='/werkstatt' element={
          <Suspense fallback={<RouteFallback />}>
            <WerkstattPage />
          </Suspense>
        } />
        <Route path='/werkstatt/bestaetigen/:token' element={
          <Suspense fallback={<RouteFallback />}>
            <NewsletterConfirmPage />
          </Suspense>
        } />
        <Route path='/werkstatt/abmelden/:token' element={
          <Suspense fallback={<RouteFallback />}>
            <NewsletterUnsubscribePage />
          </Suspense>
        } />
        <Route path='/werkstatt/:slug' element={
          <Suspense fallback={<RouteFallback />}>
            <WerkstattIssuePage />
          </Suspense>
        } />

        {/* Legal Routes */}
        <Route path='/impressum' element={
          <Suspense fallback={<RouteFallback />}>
            <ImpressumPage />
          </Suspense>
        } />
        <Route path='/datenschutz' element={
          <Suspense fallback={<RouteFallback />}>
            <DatenschutzPage />
          </Suspense>
        } />
        {/* Öffentlich, ohne Login: Google Play verlangt eine Webseite zur Kontolöschung */}
        <Route path='/konto-loeschen' element={
          <Suspense fallback={<RouteFallback />}>
            <KontoLoeschenPage />
          </Suspense>
        } />
        {/* Öffentlich, ohne Login: Weg in den geschlossenen Play-Test (Android-Knopf der Website) */}
        <Route path='/android' element={
          <Suspense fallback={<RouteFallback />}>
            <AndroidBetaPage />
          </Suspense>
        } />
        {/* Abmeldelink aus Akquise-Mails des Schul-CRMs (ohne Login); ohne Token: alte Links aus Pilot-Mails */}
        <Route path='/unsubscribe/:token' element={
          <Suspense fallback={<RouteFallback />}>
            <UnsubscribePage />
          </Suspense>
        } />
        <Route path='/unsubscribe' element={
          <Suspense fallback={<RouteFallback />}>
            <UnsubscribePage />
          </Suspense>
        } />
        <Route path='/agb' element={
          <Suspense fallback={<RouteFallback />}>
            <AGBPage />
          </Suspense>
        } />
        <Route path='/avv' element={
          <Suspense fallback={<RouteFallback />}>
            <AVVPage />
          </Suspense>
        } />
        <Route path='/shared/journal/:token' element={
          <Suspense fallback={<RouteFallback />}>
            <SharedJournalPage />
          </Suspense>
        } />

        {/* Sentry-Testseite: absichtlich NUR ausserhalb von Production.
            Sie war bis August 2026 auch im Livebetrieb erreichbar — mit einem Knopf
            "Break the world", der einen echten Fehler nach Sentry schickt. In der Beta
            koennte den jemand versehentlich druecken und das Fehlerprotokoll verrauschen. */}
        {process.env.NODE_ENV !== 'production' && (
          <Route path='/sentry-test' element={
            <Suspense fallback={<RouteFallback />}>
              <SentryTestPage />
            </Suspense>
          } />
        )}

        {/* Auth-Routen */}
        <Route path='/login' element={<AuthPage mode="login" />} />

        {/* KI-Assistent verbinden (MCP-OAuth, Okt 2026): öffentlich, Login inline.
            Nie in Universal Links/App Links aufnehmen, sonst kommt der Rücksprung
            zum Assistenten nicht im Browser an. */}
        <Route path='/connect' element={
          <Suspense fallback={<RouteFallback />}>
            <ConnectAssistantPage />
          </Suspense>
        } />
        <Route path='/register' element={<AuthPage mode="register" />} />

        {/* QR-Scanner für Einladungen (öffentlich zugänglich) */}
        <Route path='/scan-invitation' element={
          <Suspense fallback={<RouteFallback />}>
            <QRScannerPage />
          </Suspense>
        } />

        {/* Passwort-Management (öffentlich) */}
        <Route path='/forgot-password' element={
          <Suspense fallback={<RouteFallback />}>
            <ForgotPasswordPage />
          </Suspense>
        } />
        <Route path='/reset-password' element={
          <Suspense fallback={<RouteFallback />}>
            <ResetPasswordPage />
          </Suspense>
        } />

        {/* Onboarding: einzige Render-Stelle des ProfileSetup (siehe ProfileSetupRoute oben) */}
        <Route path='/profile-setup' element={
          <ProfileSetupRoute
            user={user}
            showProfileSetup={showProfileSetup}
            onComplete={handleProfileSetupComplete}
            onFinish={handleProfileSetupFinish}
          />
        } />

        {/* Willkommens-Tour nach Onboarding (auch erneut über die Willkommens-Notification erreichbar) */}
        <Route path='/welcome' element={
          user ? (
            <Suspense fallback={<RouteFallback />}>
              <WelcomeTour user={user} />
            </Suspense>
          ) : (
            <LoginRedirect />
          )
        } />

        {/* DSGVO Settings - User Privacy & Data Management */}
        <Route path='/settings/privacy' element={
          user ? (
            <Suspense fallback={<RouteFallback />}>
              <DsgvoSettings />
            </Suspense>
          ) : (
            <LoginRedirect />
          )
        } />

        {/* Notification Settings */}
        <Route path='/settings/notifications' element={
          user ? (
            <Suspense fallback={<RouteFallback />}>
              <NotificationSettingsPage />
            </Suspense>
          ) : (
            <LoginRedirect />
          )
        } />

        {/* Schulregistrierung-Bestätigungsseite */}
        <Route path="/confirm-school" element={<SchoolConfirmationPage />} />

        {/* Standard-App für alle Benutzer (hauptsächlich Schüler) */}
        <Route
          path="/app"
          element={user ? renderMainScreen() : <LoginRedirect />}
        />

        {/* Sessions und Lernlogbuch sind Ansichten der Hauptseite, keine eigenen
            Seiten (August 2026). Vorher gab es sie doppelt: einmal eingebettet in
            MainScreen und einmal als eigene Route mit eigenem Rahmen — andere
            Breite, anderer Kopf, andere Abstaende und, weil die Bottom-Navigation
            nur an /app haengt, ohne Weg zurueck ausser dem Pfeil.
            Die Adressen bleiben gueltig (Benachrichtigungen, QR-Codes und
            LTI-Navigation zeigen darauf), sie fuehren jetzt nur an die eine
            Stelle, an der es diese Ansicht gibt. Der `state`, den einige Aufrufer
            mitgeben, wurde von SessionsPage ohnehin nie gelesen. */}
        <Route
          path="/sessions/*"
          element={<Navigate to={user ? '/app?view=sessions' : '/login'} replace />}
        />

        <Route
          path="/learning-journal/*"
          element={<Navigate to={user ? '/app?view=journal' : '/login'} replace />}
        />

        {/* DEAKTIVIERT: GamePage wurde entfernt */}
        {/* <Route
          path="/game"
          element={<GamePage />}
        /> */}

        {/* 🆕 Mobile-First Pages (Session Management) */}
        <Route
          path="/create-session"
          element={
            loading ? (
              <LoadingSpinner />
            ) : user ? (
              <Suspense fallback={<RouteFallback />}>
                <CreateSessionPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/barcamp-planner"
          element={
            loading ? (
              <LoadingSpinner />
            ) : user ? (
              <Suspense fallback={<RouteFallback />}>
                <BarcampPlannerPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/join-session"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <JoinSessionPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/session/:sessionId/edit"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <EditSessionPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/session/:sessionId/feedback"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <SessionFeedbackPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/session/:sessionId"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <SessionDetailPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/focus-session/:sessionId"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <FocusSessionPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/chat/:chatId"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ChatPageRoute />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/chat/:chatId/report"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ReportMessagePage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/skill-request"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <SkillRequestPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/scan-qr"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <QRScannerPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* 🆕 Mobile-First Pages (Account Management) */}
        <Route
          path="/account-settings"
          element={
            loading ? (
              <LoadingSpinner />
            ) : user ? (
              <Suspense fallback={<RouteFallback />}>
                <AccountSettingsPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/account-settings/change-password"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ChangePasswordPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/account-settings/appearance"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <AppearanceSettingsPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        {/* Persoenliche Zugriffstokens (MCP): ab Schul-Admin. Ein Token gilt lange
            und haengt am eigenen Konto — nichts, was in einer Klasse herumliegen
            sollte. Bis September 2026 war die Seite fuer jeden Angemeldeten offen;
            die drei Endpunkte in backend/src/api/v1/routes/auth.js sind seither
            ebenfalls auf ADMIN begrenzt, sonst waere das hier nur Kosmetik. */}
        <Route
          path="/account-settings/api-tokens"
          element={
            <RoleRoute
              requiredRoles="ADMIN"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <ApiTokensPage />
                </Suspense>
              }
              redirectPath="/account-settings"
            />
          }
        />
        {/* Verbundene KI-Assistenten (MCP-Freigaben): für alle Rollen */}
        <Route
          path="/account-settings/connected-assistants"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ConnectedAssistantsPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/account-settings/blocked-users"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <BlockedUsersPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/feedback"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <FeedbackPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />
        <Route
          path="/profile-edit"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ProfileEditPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* 🆕 Mobile-First Pages (Social/Connect) */}
        <Route
          path="/connect/:recipientId"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <ConnectPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* Notifications Page (Mobile Fullscreen) */}
        <Route
          path="/notifications"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <NotificationsPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* 🆕 Mobile-First Pages (Aura/Stats) */}
        <Route
          path="/aura-stats"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <AuraStatsPage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* Otter-Schrank (Aura v2): zeigt bei abgeschaltetem Schrank einen Leerzustand */}
        <Route
          path="/aura-stats/schrank"
          element={
            user ? (
              <Suspense fallback={<RouteFallback />}>
                <OtterWardrobePage />
              </Suspense>
            ) : (
              <LoginRedirect />
            )
          }
        />

        {/* Rollenbasierte Dashboard-Routen mit Layout */}
        {/* FIX (19. Okt 2025): Kein Suspense um DashboardLayout, da es nicht mehr lazy ist */}
        <Route
          path="/dashboard"
          element={<DashboardLayout />}
        >
          {/* `/dashboard` ohne Bereich fuehrte bisher nirgendwohin: leeres
              Layout, leere Navigation, kein Inhalt — und fuer einen Schueler,
              der ueber einen alten Link dort landete, war der einzige Ausweg
              der versteckte "Zurueck zur App"-Link. Jetzt schickt die Route
              jeden auf seinen Bereich, Rollen ohne Bereich zurueck in die App. */}
          <Route index element={<Navigate to={getDashboardPath()} replace />} />

          {/* Teacher Dashboard */}
          <Route
            path="teacher/*"
            element={
              <RoleRoute
                requiredRoles="TEACHER"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <TeacherDashboard user={user} />
                  </Suspense>
                }
                redirectPath="/app"
              />
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="admin/*"
            element={
              <RoleRoute
                requiredRoles="ADMIN"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <SchoolAdminDashboard user={user} />
                  </Suspense>
                }
                redirectPath="/app"
              />
            }
          />

          {/* Super Admin Dashboard */}
          <Route
            path="super-admin/*"
            element={
              <RoleRoute
                requiredRoles="SUPERADMIN"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <SuperAdminDashboard user={user} />
                  </Suspense>
                }
                redirectPath="/app"
              />
            }
          />
        </Route>

        {/* Baustein-Katalog: alle Komponenten des Design-Systems auf einer Seite.
            Ohne diesen Ort sieht niemand, was es schon gibt — und baut nach.
            SUPERADMIN-only, damit die Seite niemandem im Weg steht. */}
        <Route
          path="/design"
          element={
            <RoleRoute
              requiredRoles="SUPERADMIN"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <DesignSystemPage />
                </Suspense>
              }
              redirectPath="/app"
            />
          }
        />

        {/* LTI-Session-Route - Zugänglich ohne Authentifizierung */}
        <Route path="/lti-session" element={<LtiSession />} />

        {/* NEU: LTI-Auth-Route - Wird nach erfolgreichem LTI-Launch aufgerufen */}
        <Route path="/lti-auth" element={<LtiSession />} />
      </Routes>

      {/* Studienfrage: genau einmal, nach dem Onboarding (bzw. einmalig nachtraeglich,
          wenn die Schulleitung erst spaeter zustimmt). Rendert nichts, solange der
          Server kein shouldAsk meldet. Bewusst NICHT im ProfileSetup selbst: Die App
          ist an dieser Stelle schon fertig eingerichtet und nutzbar, damit die Antwort
          nachweislich freiwillig ist (Kopplungsverbot, Art. 7 Abs. 4 DSGVO). */}
      <Suspense fallback={null}>
        <ResearchConsentGate ready={!!user && !showProfileSetup} />
      </Suspense>
      {/* 🦦 Otter-Toast-Container - Einziger Container für alle Toasts */}
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        {/* 🦦 Startbildschirm mit Zeicheneffekt. Steht bewusst UEBER dem
            AuthProvider: der rendert seine Children erst bei loading===false,
            waehrend der Splash genau diese Wartezeit ueberbruecken soll. */}
        <LogoSplash />
        <Router>
          {/* ScrollToTop: Scrollt automatisch nach oben bei jeder Navigation */}
          <ScrollToTop />
          {/* Deep-Links stehen bewusst hier und nicht in AppContent: Dort hätte sie
              jede Weiche (Ladespinner, Schul-Setup, Onboarding) beim Umleiten
              abgebaut und danach wieder aufgebaut. */}
          <DeepLinkHandler />
          <AuthProvider>
            <NotificationProvider>
              {/* Rückfragen (useConfirm) als App-weiter Dialog statt window.confirm */}
              <ConfirmProvider>
                <AppContent />
              </ConfirmProvider>
              {/* Verbindungsstatus: lag bisher nur auf der Landingpage — also
                  genau dort, wo ein Verbindungsverlust niemanden stört. Wer
                  mitten in einer Lernsession offline geht, erfuhr es nicht. */}
              <NetworkStatus />
              {/* Ohne position-Angabe gilt die Vorgabe der Komponente
                  (top-center). Vorher stand hier top-right gegen top-center im
                  Baustein — Meldungen erschienen je nach Aufrufweg woanders. */}
              <NotificationToastContainer />
            </NotificationProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
